import { z } from 'zod';
import { RowVersionSchema } from '../common/row-version.js';
import { AssetCategorySchema } from '../assets/categories.js';
import { CatalogArtifactItemSchema } from '../catalog/responses.js';
import { BandKeySchema, BandSchema } from '../common/bands.js';
import { paginatedResponseSchema } from '../common/pagination.js';
import { REVIEW_REASONS } from './review.js';
import { ContentFamilySchema, DerivativeKindSchema, FileClassSchema, FileStatusSchema, SubjectSourceSchema } from './vocabulary.js';
// Response schemas for the files library (ADR-068). Field-exposure rule (ANATOMY
// §L7): the wire carries what the library renders — the ONE file shape every read
// returns (the tile · the row · the surface · the bin row · a version), so the
// container maps it onto the view-model in one place. Never the object key (the
// store is invisible), never the hash of a sealed class.
const Id16 = z.string().max(16);
export const FileMediaSchema = z
    .object({
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    duration_ms: z.number().int().nonnegative().optional(),
})
    .strict();
export const FileDerivativeSchema = z
    .object({
    kind: DerivativeKindSchema,
    ready: z.boolean(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
})
    .strict();
export const FilePersonSchema = z.object({ id: Id16, label: z.string() }).strict();
/** What a capture is ABOUT (0..n): an asset_files row with relation = subject. */
export const FileSubjectSchema = z
    .object({
    asset_id: Id16,
    label: z.string(),
    /** The asset's category — the ladder: a named asset gives its topic for free. */
    category: z.string().nullable(),
    source: SubjectSourceSchema,
    /** 0..1 for a machine / derived proposal; null for a person's link. */
    confidence: z.number().min(0).max(1).nullable(),
    /** How the machine knew — printed beside a proposal, never as a fact. */
    via: z.string().nullable(),
    confirmed_at: z.string().nullable(),
    /** Accepted by the project's trust dial (confirmed_at set, confirmed_by NULL) — printed as such. */
    auto_accepted: z.boolean(),
    /** A dismissed proposal is KEPT and marked. */
    rejected_at: z.string().nullable(),
    /** Born on this asset — the anchor as a synthetic confirmed human naming
     *  (the read's shape writes it, never a stored row): rendered as a named
     *  subject, locked in the picker, never sent back by a classification. */
    born: z.boolean().optional(),
})
    .strict();
/** A subject outside the viewer's reach (the naming rule — a unit since moved
 *  where they cannot see): its KIND alone, "a unit", with the ladder's marks;
 *  its name and its id never travel. */
export const FileHiddenSubjectSchema = z
    .object({
    hidden: z.literal(true),
    kind: z.literal('unit'),
    source: SubjectSourceSchema,
    confidence: z.number().min(0).max(1).nullable(),
    via: z.string().nullable(),
    confirmed_at: z.string().nullable(),
    auto_accepted: z.boolean(),
    rejected_at: z.string().nullable(),
})
    .strict();
export const FileNoteSchema = z
    .object({
    id: Id16,
    /** `note` = added in the app; `transcript` = a voice note's transcript (machine-made, read-only — the extracts seam). */
    kind: z.enum(['note', 'transcript']),
    text: z.string(),
    author: FilePersonSchema.nullable(),
    created_at: z.string(),
    edited_at: z.string().nullable(),
})
    .strict();
export const ReviewReasonSchema = z.enum(REVIEW_REASONS);
export const FileAnchorRefSchema = z
    .object({
    // 'ticket' = a capture's home (the work it was routed to — v1 the visit's walk-up container; the label is its site).
    kind: z.enum(['site', 'asset', 'model', 'user', 'project', 'ticket']),
    id: Id16,
    label: z.string(),
})
    .strict();
/** A field capture's visit (the capture ingest fills it; null = not checked in, or not a capture). */
export const FileVisitSchema = z.object({ id: Id16, day: z.string(), person: FilePersonSchema }).strict();
/** THE file shape — every read returns it. */
export const LibraryFileSchema = z
    .object({
    row_version: RowVersionSchema,
    id: Id16,
    class: FileClassSchema,
    status: FileStatusSchema,
    family: ContentFamilySchema,
    content_type: z.string().max(127),
    size_bytes: z.number().int().nonnegative(),
    original_name: z.string().max(255),
    display_name: z.string().max(255).nullable(),
    /** Can the files origin render it inline (family + class rule)? */
    previewable: z.boolean(),
    media: FileMediaSchema.nullable(),
    /** Which producer-made sizes are ready (the container mints their links). */
    derivatives: z.array(FileDerivativeSchema),
    /** One folder = a document; null = a described thing (the nature rule). */
    folder: z.string().max(32).nullable(),
    /** `subject` = shown here through a subject link (an asset's lens; the site's "naming its assets"). */
    relation: z.enum(['anchor', 'subject']),
    /** A catalog copy (a product's canonical document) — preview + download only. */
    provenance: z.enum(['catalog']).nullable(),
    /** Sits in a restricted folder (visible only with the facet key). */
    restricted: z.boolean(),
    uploaded_by: FilePersonSchema.nullable(),
    /** When it was TAKEN (UTC ISO) — a capture's phone time, an upload's commit time. */
    captured_at: z.string(),
    /** The subjects the viewer may name. */
    subjects: z.array(FileSubjectSchema),
    /** The subjects they may not — each its kind alone (the naming rule). */
    hidden_subjects: z.array(FileHiddenSubjectSchema),
    topics: z.array(z.string().max(32)),
    /** A capture's words (the journal entry's text); a web photo's caption is its display name. */
    caption: z.string().nullable(),
    reviewed_by: FilePersonSchema.nullable(),
    reviewed_at: z.string().nullable(),
    visit: FileVisitSchema.nullable(),
    /** A field capture's check-in state at the moment (null = not a capture). */
    checked_in: z.boolean().nullable(),
    /** The capture's fix against the site's registered location (null = none). */
    position: z.object({ match: z.boolean(), distance_m: z.number().nullable() }).strict().nullable(),
    /** Why it waits for a human under the project's policy (empty = nothing to review). */
    review_reasons: z.array(ReviewReasonSchema),
    deleted_at: z.string().nullable(),
    deleted_by: FilePersonSchema.nullable(),
    /** When the sweeper lets go of a deleted file (deleted_at + the grace window). */
    purge_at: z.string().nullable(),
    /** The entity it is linked to today (owns the gate; "Open at the site / asset"). */
    anchor: FileAnchorRefSchema,
    /** Replace-with-a-new-version: this version's number + how many older ones exist. */
    version: z.number().int().positive(),
    older_versions: z.number().int().nonnegative(),
    /** On an older version: the current one's id. */
    superseded_by: Id16.nullable(),
    created_at: z.string(),
    committed_at: z.string().nullable(),
    /** On a GROUPED flow: the key of the divider this row sits under (the day · month · year it was taken, in the
     *  screen's zone), stamped by the server — the same expression that counted the bands; the client never computes
     *  one (ADR-078 §4). */
    band: BandKeySchema.optional(),
})
    .strict();
/** The surface's read (GET …/files/:id): the file + its notes + its older versions. */
export const FileDetailSchema = LibraryFileSchema.extend({
    notes: z.array(FileNoteSchema),
    /** The older versions, newest first — each a file of its own (read-only in the surface). */
    versions: z.array(LibraryFileSchema),
}).strict();
// ── the shelf ───────────────────────────────────────────────────────────────
export const ShelfLaneItemSchema = z
    .object({
    id: Id16,
    label: z.string(),
    documents: z.array(LibraryFileSchema),
    /** A PRODUCT lane item's canonical documents (the catalog's sha-addressed
     *  artifacts — never files rows, ADR-068 §1): the container renders them
     *  through the same row grammar as synthetic catalog rows, preview +
     *  download only, "from the catalog" in the subline. */
    artifacts: z.array(CatalogArtifactItemSchema).optional(),
})
    .strict();
export const ShelfLaneSchema = z
    .object({
    key: z.enum(['assets', 'products']),
    items: z.array(ShelfLaneItemSchema),
})
    .strict();
export const ShelfResponseSchema = z
    .object({
    /** The anchor's OWN documents, newest first (whole — documents are few by construction). */
    documents: z.array(LibraryFileSchema),
    /** The hub's lanes — the paper the site's assets carry of their own; the products' documents, once per product. */
    lanes: z.array(ShelfLaneSchema),
    /** The folders the caller may see on this anchor (a restricted one only with its facet key). */
    folders: z.array(z.string().max(32)),
})
    .strict();
// ── the flow ────────────────────────────────────────────────────────────────
/** The flow's page (`items` · `total` — the question's whole count, the lens included · `limit` · `offset`) and, with
 *  `group_by`, `bands`: EVERY divider of the filtered library in time order with its TRUE count (the sum is `total`),
 *  each row carrying its `band` (ADR-078 §4 — the stream with every band open). */
export const MediaResponseSchema = paginatedResponseSchema(LibraryFileSchema)
    .extend({
    /** What waits for a human under the question (the lens excluded) — the toggle's count. */
    review_count: z.number().int().nonnegative(),
    bands: z.array(BandSchema).optional(),
})
    .strict();
// ── the collections ─────────────────────────────────────────────────────────
export const FileCollectionSchema = z
    .object({
    /** `asset:<id>` · `topic:<code>` · `none`. */
    key: z.string(),
    by: z.enum(['asset', 'topic']),
    /** The filter value that opens it: an asset id, a topic code, or `none`. */
    value: z.string(),
    label: z.string(),
    count: z.number().int().positive(),
    /** The newest file — the cover. */
    cover: LibraryFileSchema,
    latest_at: z.string(),
})
    .strict();
export const CollectionsResponseSchema = z.object({ items: z.array(FileCollectionSchema) }).strict();
// ── the bin ─────────────────────────────────────────────────────────────────
export const BinResponseSchema = z.object({ items: z.array(LibraryFileSchema) }).strict();
// ── the options — the question's material (the drill · the picker) ──────────
export const FilesOptionsResponseSchema = z
    .object({
    /** The KINDS present at the site (the site's units' categories; an asset's and a ticket's: their site's), in the
     *  charter's order — the picker's chips. A kind's units come from the unit lookup, 20 at a time (ADR-078 §6);
     *  the Asset filter rides the same lookup. Empty without an asset read, or at a place with no units. */
    kinds: z.array(AssetCategorySchema),
    /** The people who uploaded here — the Person filter. */
    people: z.array(FilePersonSchema),
    /** The topics IN USE here (a hand-picked topic or a named asset's category) — the Topic filter offers only these. */
    topics: z.array(z.string().max(32)),
    /** The whole topic vocabulary, the anchor's own categories first (labels ride the registry) — the picker's chips. */
    all_topics: z.array(z.string().max(32)),
    /** The folders IN USE on this anchor's own documents (the caller's band) — the Folder filter offers only these. */
    folders: z.array(z.string().max(32)),
    /** Every folder the caller may use on this anchor — the upload and the move offer these. */
    all_folders: z.array(z.string().max(32)),
    /** How long the bin keeps a deleted file (the toast's and the dialog's number). */
    bin_grace_days: z.number().int().positive(),
})
    .strict();
// ── POST announce → the pending file + where to put the bytes ───────────────
export const AnnouncedFileSchema = z
    .object({
    id: Id16,
    /** Relative to the API base: PUT the raw bytes here. */
    upload_path: z.string(),
    /** Which derivative kinds the class accepts (empty for never-preview classes). */
    derivative_kinds: z.array(DerivativeKindSchema),
})
    .strict();
// ── POST preview-link · POST preview-links ──────────────────────────────────
export const PreviewLinkResponseSchema = z
    .object({
    /** Absolute — on the FILES origin, never the app origin. */
    url: z.string(),
    expires_at: z.string(),
})
    .strict();
export const PreviewLinksResponseSchema = z
    .object({
    items: z.array(z
        .object({
        file_id: Id16,
        kind: z.enum(['original', 'thumb', 'web']),
        /** Null when this file cannot be previewed (a missing size, a never-preview class). */
        url: z.string().nullable(),
        expires_at: z.string().nullable(),
    })
        .strict()),
})
    .strict();
// ── the classification verb's answer — the file as it now reads ─────────────
export const ClassifiedResponseSchema = z.object({ items: z.array(LibraryFileSchema) }).strict();
